import { Button } from '../../components/Button';
import { GuestPicker } from '../../components/GuestPicker';
import { PageHeading } from '../../components/PageHeading';
import { SideImage } from '../../components/SideImage';
import { DatePicker } from '../../composites/DatePicker';
import type {
  JourneyPickerLabels,
  JourneyPickerSection,
} from '../../composites/JourneyPicker/JourneyPicker.types';
import { SearchActionBar } from '../../composites/SearchActionBar';
import {
  Body,
  BottomBar,
  BottomBarInner,
  CalendarScroll,
  CenteredContent,
  Container,
  Hero,
  Main,
  SkipButton,
  StepBody,
} from './SearchFlowTemplate.styles';
import type { SearchFlowLabels, SearchFlowTemplateProps } from './SearchFlowTemplate.types';

function buildJourneyPickerLabels(labels: SearchFlowLabels): JourneyPickerLabels {
  return {
    selectDate: labels.addDates,
    selectDates: labels.addDates,
    guests: (total: number) => (total === 0 ? labels.addGuests : labels.guests(total)),
    datePicker: labels.datePicker,
    guestPicker: labels.guestPicker,
  };
}

export function SearchFlowTemplate({
  step,
  heroImageUrl,
  checkIn,
  checkOut,
  singleDateMode = false,
  availability,
  isLoadingAvailability,
  onRangeSelect,
  onMonthChange,
  adults,
  childCount,
  onAdultsChange,
  onChildrenChange,
  minAdults,
  maxAdults,
  minChildren,
  maxChildren,
  showChildren = true,
  onBack,
  onSelectStep,
  onSkip,
  onNext,
  onSearch,
  labels,
}: SearchFlowTemplateProps) {
  const isWhen = step === 'when';
  const isWho = step === 'who';
  const hasDates = singleDateMode ? !!checkIn : !!checkIn && !!checkOut;
  const title = isWhen ? labels.whenTitle : labels.whoTitle;
  const primaryLabel = isWhen ? labels.next : labels.search;
  const primaryDisabled = isWhen ? !hasDates : adults < (minAdults ?? 1);
  const primaryAction = isWhen ? onNext : onSearch;

  const handleEditSection = (section: JourneyPickerSection) => {
    if (section === 'dates') onSelectStep('when');
    else if (section === 'guests') onSelectStep('who');
  };

  return (
    <Container>
      <Hero>
        <SideImage src={heroImageUrl ?? undefined} alt="" sticky />
      </Hero>
      <Main>
        <SearchActionBar
          onBack={onBack}
          backAriaLabel={labels.back}
          checkIn={checkIn}
          checkOut={checkOut}
          singleDateMode={singleDateMode}
          adults={adults}
          childCount={childCount}
          onRangeSelect={onRangeSelect}
          onAdultsChange={onAdultsChange}
          onChildrenChange={onChildrenChange}
          onEditSection={handleEditSection}
          activeStep={isWhen ? 'dates' : 'guests'}
          labels={buildJourneyPickerLabels(labels)}
        />
        <Body>
          <CenteredContent>
            <PageHeading title={title} flush />
            <StepBody>
              {isWhen ? (
                <CalendarScroll>
                  <DatePicker
                    checkIn={checkIn ?? null}
                    checkOut={checkOut ?? null}
                    singleDateMode={singleDateMode}
                    numberOfMonths={2}
                    stacked
                    pageScroll
                    availability={availability}
                    isLoading={isLoadingAvailability}
                    onRangeSelect={onRangeSelect}
                    onMonthChange={onMonthChange}
                    labels={labels.datePicker}
                  />
                </CalendarScroll>
              ) : (
                <GuestPicker
                  adults={adults}
                  childCount={childCount}
                  onAdultsChange={onAdultsChange}
                  onChildrenChange={onChildrenChange}
                  minAdults={minAdults}
                  maxAdults={maxAdults}
                  minChildren={minChildren}
                  maxChildren={maxChildren}
                  showChildren={showChildren}
                  renderInline
                  labels={labels.guestPicker}
                />
              )}
            </StepBody>
          </CenteredContent>
        </Body>
        <BottomBar>
          <BottomBarInner>
            {isWhen && onSkip ? (
              <SkipButton type="button" onClick={onSkip}>
                {labels.skip}
              </SkipButton>
            ) : null}
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={primaryAction}
              disabled={primaryDisabled}
              fullWidth={isWho}
            >
              {primaryLabel}
            </Button>
          </BottomBarInner>
        </BottomBar>
      </Main>
    </Container>
  );
}
